import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { create, type Draft } from 'mutative';
import { ENDPOINT } from '~/constants';

import { type AssetItem } from '~/types/assets';
import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysAssetReparsing } from './queryKeys';

type DeleteReparsingAssetResponse = {
  code: number;
  message: string;
  requestId: UUID;
};

/**
 * Deletes reparsing asset (DLN).
 * Only deletes the reparsing task and doesn't affect the actual asset.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/delete_dln_for_reparsing_vadmin_asset_reparsing__reparsing_id__delete
 */
export const deleteReparsingAsset = async (reparsingId: UUID) => {
  try {
    const response = await vestigasApi
      .delete(ENDPOINT.ASSET_REPARSING.DELETE_REPARSING_ASSET(reparsingId))
      .json<DeleteReparsingAssetResponse>();

    return {
      ...response,
      reparsingId,
    };
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.deleteReparsingAsset.error.400',
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.deleteReparsingAsset.error.422',
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.deleteReparsingAsset.error.generic',
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to delete the reparsing asset.
 * This does not delete the asset itself, but only the reparsing task.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/delete_dln_for_reparsing_vadmin_asset_reparsing__reparsing_id__delete
 */
export const useMutationDeleteReparsingAsset = (
  reparsingId: UUID,
  setConfirmOpen: (open: boolean) => void,
  options?: UseMutationOptions,
): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reparsingIdArgument: UUID | undefined) =>
      deleteReparsingAsset(reparsingIdArgument || reparsingId), // TODO: it's wrong to pass the function args in through the hook!
    mutationKey: queryKeysAssetReparsing.deleteReparsingAsset(),
    onError(error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysAssetReparsing.getAll(),
      });
      setConfirmOpen(false);
    },
    onSuccess({ reparsingId }) {
      ToastService.info('Asset gelöscht.');

      // Perform optimistic update.
      queryClient.setQueryData(
        queryKeysAssetReparsing.getAll(),
        (oldData: AssetItem[]) =>
          // Use mutative for high performance immutability.
          create(oldData, (draft: Draft<AssetItem[]>) =>
            draft.filter(({ id }) => id !== reparsingId),
          ),
      );
    },
    ...options,
  });
};
